import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DocumentBanner} from 'notebook/components/banners/DocumentBanner';
import {hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import createTemplateDraftFromVersion from 'notebook/entry-templates/createTemplateDraftFromVersion';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import TemplateNoteTitleBadges from 'notebook/notes/tabs/TemplateNoteTitleBadges';
import {TemplateNoteView, TemplateNoteViewProps} from 'notebook/notes/tabs/TemplateNoteView';
import {getPublishableInfoForPublishingButton} from 'template-collectibles/publishing-ui/TemplatePublishingUtil';
import useTemplateVersionPublishingState from 'template-collectibles/publishing-ui/useTemplateVersionPublishingState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface PublishingTemplateNoteViewProps extends TemplateNoteViewProps {
  openVersionApiId: string;
  openVersionModifiedAt: string;
}

export default function PublishingTemplateNoteView(
  props: PublishingTemplateNoteViewProps
): JSX.Element | null {
  const {fileId, openVersionApiId, openVersionModifiedAt, banner, isLatest} = props;
  const publishingState = useTemplateVersionPublishingState(fileId, openVersionApiId);

  if (publishingState.type === 'loading') {
    return <CenteredHorizontalThrobber />;
  } else if (publishingState.type === 'error') {
    return null;
  }

  const {publishingRecords, openVersionPublishingRecord, refetch} = publishingState;

  const onPublish = async (versionName: string, versionDescription?: string): Promise<void> => {
    await TemplateDAO.publishVersion(fileId, openVersionApiId, versionName, versionDescription);
    const editorState = TemplateStore.getEditorState(fileId);
    if (editorState != null) {
      TemplateActions.loadVersion(fileId, editorState.openVersionId);
    }
    refetch();
  };

  const onWithdraw = async (): Promise<void> => {
    await TemplateDAO.withdrawVersion(fileId, openVersionApiId);
    refetch();
  };

  const onCreateDraft = (): Promise<void> => createTemplateDraftFromVersion(fileId, openVersionApiId);

  const isViewingPublishedVersion = openVersionPublishingRecord != null;
  const bannerToSet = isViewingPublishedVersion ? (
    <DocumentBanner.Publishing
      onCreateDraft={onCreateDraft}
      publishingStatus={openVersionPublishingRecord.publishingStatus}
    />
  ) : null;

  const publishingButtonProps = {
    ...getPublishableInfoForPublishingButton(
      publishingRecords,
      openVersionApiId,
      openVersionModifiedAt,
      isLatest
    ),
    publishingRecords,
    publishableModelTypeName: ModelTypeConstants.TEMPLATE,
    onPublish,
    onWithdraw,
    onCreateDraft,
    isPublishOptionDisabled: hasUnsavedChanges(fileId),
  };

  const titleBadge = (
    <TemplateNoteTitleBadges
      templateId={fileId}
      openVersionApiId={openVersionApiId}
      isLatest={isLatest}
      isViewingPublishedVersion={isViewingPublishedVersion}
      publishingButtonProps={publishingButtonProps}
    />
  );

  return (
    <TemplateNoteView
      {...props}
      isViewingPublishedVersion={isViewingPublishedVersion}
      // Assumes that the banner set by the TemplateFile takes precedence over the banner set by this component.
      banner={banner || bannerToSet}
      titleBadge={titleBadge}
    />
  );
}
